// Reads /crypto/manifest.json, which the build publishes next to the
// unminified @sss/crypto files (vite-plugins/transparentCrypto.ts). Untrusted input
// as far as this page is concerned, so it is shape-checked before display.

export interface CryptoManifestFile {
  path: string;
  bytes: number;
  sha256: string;
  integrity: string;
}

export interface CryptoManifest {
  version: string;
  set: string;
  entry: string;
  files: CryptoManifestFile[];
}

const isFile = (set: string) => (f: unknown): f is CryptoManifestFile => {
  if (typeof f !== 'object' || f === null) return false;
  const r = f as Record<string, unknown>;
  return (
    typeof r.path === 'string' &&
    new RegExp(`^/crypto/${set}/[A-Za-z0-9_-]+\\.js$`).test(r.path) &&
    typeof r.bytes === 'number' &&
    typeof r.sha256 === 'string' &&
    /^[0-9a-f]{64}$/.test(r.sha256) &&
    typeof r.integrity === 'string' &&
    r.integrity.startsWith('sha384-')
  );
};

export const parseCryptoManifest = (value: unknown): CryptoManifest | null => {
  if (typeof value !== 'object' || value === null) return null;
  const r = value as Record<string, unknown>;
  if (r.format !== 2 || typeof r.version !== 'string' || typeof r.entry !== 'string') return null;
  // The set id is spliced into a pattern below, so it must be exactly 16 hex characters.
  if (typeof r.set !== 'string' || !/^[0-9a-f]{16}$/.test(r.set)) return null;
  if (!Array.isArray(r.files) || !r.files.every(isFile(r.set))) return null;
  return { version: r.version, set: r.set, entry: r.entry, files: r.files };
};

export const fetchCryptoManifest = async (signal?: AbortSignal): Promise<CryptoManifest | null> => {
  const res = await fetch('/crypto/manifest.json', { signal, cache: 'no-store' });
  if (!res.ok) return null;
  return parseCryptoManifest(await res.json());
};
